import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  signal,
  WritableSignal,
  DestroyRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, ActivatedRoute } from '@angular/router';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  FormArray,
  Validators,
  AbstractControl,
  AsyncValidatorFn,
  ValidationErrors,
} from '@angular/forms';
import { Observable, of, timer } from 'rxjs';
import { switchMap, map, catchError, finalize } from 'rxjs/operators';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzInputNumberModule } from 'ng-zorro-antd/input-number';
import { NzSwitchModule } from 'ng-zorro-antd/switch';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzStepsModule } from 'ng-zorro-antd/steps';
import { NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { CredencialesNuevasComponent } from './credenciales-nuevas.component';
import { EmpleadoService } from '@services/empleado.service';
import { RhCatalogosService } from '@services/rh-catalogos-service';
import { VacacionesService } from '@services/vacaciones.service';
import { DirectorAsignacion } from '@schemas/vacaciones.schema';
import {
  CatalogoItem,
  ContactoEmergencia,
  Estudio,
  Pagadora,
  CreateEmpleadoRequest,
  UpdateEmpleadoRequest,
} from '@schemas/empleado.schema';
import { NzSpinModule } from 'ng-zorro-antd/spin';

import {
  EmptyState,
  PageActions,
  PageHeader,
  SectionHeader,
  TableActions,
  type ActionButtonDef,
} from '@intelishs-group/ui';
import {
  RX_CODIGO_POSTAL,
  RX_NSS,
  RX_TELEFONO_MX,
  clabe,
  curp,
  normalizarClave,
  patron,
  rfc,
} from '@intelishs-group/ui/forms';
import { FormField } from '../../../components/form-field/form-field';
import { RH_ACTIONS } from '@shared/constants/rh-actions.constants';

/**
 * Caracteres admitidos en un nombre de persona: cualquier letra Unicode y sus
 * marcas de acento —para que "José", "Ñuño", "Ángeles" o un apellido extranjero
 * pasen sin listar alfabetos a mano—, más espacio, punto ("J. Carlos"),
 * apóstrofo ("D'Angelo", con las dos comillas que producen los teclados) y
 * guión ("Jean-Pierre"). Deja fuera dígitos y los símbolos que no significan
 * nada en un nombre: #$%&/()=?¡…
 *
 * Se valida y NO se bloquea la tecla: un nombre legítimo con un carácter que
 * esta lista no previó se vuelve imposible de escribir, sin explicación. Un
 * validador falla suave y dice qué pasa.
 */
const PATRON_NOMBRE = /^[\p{L}\p{M}\s.'\u2019-]+$/u;

@Component({
  selector: 'app-empleado-form',
  imports: [
    ReactiveFormsModule,
    NzSelectModule,
    NzDatePickerModule,
    NzTimePickerModule,
    NzIconModule,
    NzButtonModule,
    NzInputModule,
    NzInputNumberModule,
    NzSwitchModule,
    NzTooltipModule,
    NzStepsModule,
    NzSpinModule,
    NzModalModule,
    CredencialesNuevasComponent,
    EmptyState,
    FormField,
    PageActions,
    PageHeader,
    SectionHeader,
    TableActions,
  ],
  templateUrl: './empleado-form.page.html',
  styleUrl: './empleado-form.page.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EmpleadoFormPage implements OnInit {
  /**
   * Las credenciales del alta, para el modal que las entrega. En nulo mientras
   * no haya ninguna que mostrar.
   *
   * Vive en la pagina y no en el componente porque el modal tiene que
   * sobrevivir al avance al Paso 2: si colgara del Paso 1 se cerraria solo al
   * cambiar de paso, justo antes de que nadie alcance a copiar nada.
   */
  readonly credenciales = signal<{ usuario: string; contrasena: string; nombre: string } | null>(
    null,
  );

  /**
   * Si ya se intentó copiar la CONTRASEÑA. Mientras sea `false`, el modal no se
   * puede cerrar.
   *
   * ── Por qué un guardia y no solo un aviso ────────────────────────────────
   *
   * Cerrar sin copiarla es el único error de este flujo que RH no puede
   * deshacer: el servidor guarda solo el cifrado, así que reponerla exige a
   * quien administra las cuentas, en otra aplicación. Un aviso informa; esto
   * impide.
   *
   * ── Y por qué la CONTRASEÑA y no el usuario ──────────────────────────────
   *
   * El usuario se puede volver a consultar —sale del expediente—; la contraseña
   * no. Pedir las dos copias haría el guardia más molesto sin proteger nada más.
   */
  readonly contrasenaCopiada = signal(false);

  /**
   * Longitud máxima por campo, para el `maxlength` de los inputs de ambos
   * pasos. Vive aquí y no como literal en la plantilla porque varios campos
   * comparten número y porque el de los que tienen formato sale de su propio
   * patrón: un NSS son 11 dígitos, un CURP 18 caracteres, una CLABE 18, un RFC
   * 12 o 13.
   *
   * Los de texto libre NO llevan además un `Validators.maxLength`: el atributo
   * frena lo que se teclea de más, pero un validador rechazaría también un
   * valor que YA está en la base y es más largo, y dejaría un expediente
   * antiguo imposible de guardar. Por eso los topes de texto libre son
   * holgados y viven solo en la plantilla; los de formato sí tienen su
   * validador, que es quien manda.
   */
  readonly max = {
    // Con patrón en el TS
    idInterno: 20,
    nombre: 100,
    nss: 11,
    rfc: 13,
    curp: 18,
    codigoPostal: 5,
    telefono: 10,
    clabe: 18,
    // Texto libre, tope holgado
    email: 150,
    lugarNacimiento: 100,
    calle: 150,
    numeroCasa: 10,
    colonia: 100,
    poblacion: 100,
    cuenta: 20,
    credito: 15,
    texto: 100,
    duracionContrato: 50,
  } as const;

  private readonly fb = inject(FormBuilder);
  private readonly empleadoService = inject(EmpleadoService);
  private readonly rhCatalogos = inject(RhCatalogosService);
  private readonly vacacionesService = inject(VacacionesService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly message = inject(NzMessageService);
  private readonly destroyRef = inject(DestroyRef);

  form!: FormGroup;
  isEdit = signal(false);
  loading = signal(false);
  saving = signal(false);
  submitted = signal(false);
  loadingCatalogos = signal(false);
  empleadoId = 0;

  // ── Acciones de las filas de array (pagadoras, contactos, estudios) ──
  //
  // Se construyen por fila y no como constante porque el icono de guardar pasa
  // a 'loading' mientras la fila se envía, y `disabled` depende del estado de
  // ESA fila. Es la misma botonera de las tablas —geometría y colores del
  // theme— en vez de los botones circulares propios que había aquí.

  private accionesFila(
    sujeto: string,
    guardando: boolean,
    puedeGuardar: boolean,
  ): ActionButtonDef<number>[] {
    return [
      {
        ...RH_ACTIONS.guardarFila,
        icon: guardando ? 'loading' : 'save',
        tooltip: `Guardar ${sujeto}`,
        disabled: () => guardando || !puedeGuardar,
      },
      {
        ...RH_ACTIONS.eliminarFila,
        tooltip: `Eliminar ${sujeto}`,
        disabled: () => guardando,
      },
    ];
  }

  // Una pagadora guardada no se elimina: deja de ser la actual con el switch y
  // se queda configurada. El bote solo descarta una fila que aún no se guarda.
  accionesPagadora(i: number): ActionButtonDef<number>[] {
    const guardando = this.estaGuardandoPagadora(i);
    const guardada = this.pagadoras.at(i).value.id != null;
    const acciones: ActionButtonDef<number>[] = [
      {
        ...RH_ACTIONS.guardarFila,
        icon: guardando ? 'loading' : 'save',
        tooltip: guardada
          ? 'Guardar los cambios de nombre y fechas IMSS'
          : 'Guardar la pagadora nueva',
        disabled: () => guardando || !this.pagadoras.at(i).valid,
      },
    ];
    if (!guardada) {
      acciones.push({
        ...RH_ACTIONS.eliminarFila,
        tooltip: 'Descartar esta fila; todavía no se ha guardado',
        disabled: () => guardando,
      });
    }
    return acciones;
  }

  accionesContacto(i: number): ActionButtonDef<number>[] {
    return this.accionesFila(
      'contacto',
      this.estaGuardandoContacto(i),
      this.contactosEmergencia.at(i).valid,
    );
  }

  accionesEstudio(i: number): ActionButtonDef<number>[] {
    return this.accionesFila('estudio', this.estaGuardandoEstudio(i), this.estudios.at(i).valid);
  }

  onAccionPagadora(accion: string, i: number): void {
    if (accion === 'guardarFila') this.guardarPagadora(i);
    else this.descartarPagadora(i);
  }

  onAccionContacto(accion: string, i: number): void {
    if (accion === 'guardarFila') this.guardarContacto(i);
    else this.eliminarContacto(i);
  }

  onAccionEstudio(accion: string, i: number): void {
    if (accion === 'guardarFila') this.guardarEstudio(i);
    else this.eliminarEstudio(i);
  }

  /**
   * Estado del validador async de ID interno, para el bloque de ayuda del
   * campo. Como getters y no leyendo `form.get('idInterno')` desde el template:
   * el nombre del control queda en un solo lugar y un typo falla al compilar en
   * vez de reventar en runtime.
   */
  get idInternoPendiente(): boolean {
    return this.form.get('idInterno')!.pending;
  }

  get idInternoDuplicado(): boolean {
    return this.form.get('idInterno')!.hasError('idInternoTaken');
  }

  /**
   * Oculta las secciones que aún NO tienen respaldo en BD: Datos Salariales,
   * Datos Bancarios e INFONAVIT (el backend no las persiste — son "bloque
   * pendiente" hasta que el DBA exponga las columnas). Cuando existan, basta
   * poner esto en `true` y conectar el guardado correspondiente.
   */
  readonly mostrarSeccionesPendientesBd = false;

  // ── Wizard de alta (solo aplica en modo creación) ─────────────────
  // Paso 1: datos que consume el SP proc_alta_usuario (crea el registro
  // base + obtiene el kidempleadorh). Paso 2: el resto, que se persiste
  // con UpdateAsync sobre el empleado ya creado (otras tablas mov*).
  // En edición no se usa (se muestra todo junto).
  paso = signal<1 | 2>(1);

  /**
   * Rutas de los controles que pertenecen al Paso 1 (datos del SP/jdatos).
   * crearPaso1() valida SOLO estos antes de llamar al SP; el resto se valida
   * en el Paso 2. Top-level + nested (domicilio.*, datosLaborales.*).
   */
  private readonly controlesPaso1: ReadonlyArray<string> = [
    'idInterno',
    'nombre',
    'primerApellido',
    'segundoApellido',
    'rfc',
    'curp',
    'sexoId',
    'estadoCivilId',
    'nacionalidadId',
    'areaId',
    'direccionId',
    'superiorId',
    'domicilio.email',
    'domicilio.telefono',
    'datosLaborales.puestoId',
    'datosLaborales.periodoPagoId',
    'datosLaborales.esquemaPagoId',
  ];

  // ── Catálogo local: días de la semana para el dropdown múltiple ───
  // 1=Lunes, …, 7=Domingo. Coincide con la convención del DBA para
  // rh.movempleadohorario.jdatos.idiasdescanso (Bloque #6).
  readonly diasSemana: ReadonlyArray<{ id: number; label: string }> = [
    { id: 1, label: 'Lunes' },
    { id: 2, label: 'Martes' },
    { id: 3, label: 'Miércoles' },
    { id: 4, label: 'Jueves' },
    { id: 5, label: 'Viernes' },
    { id: 6, label: 'Sábado' },
    { id: 7, label: 'Domingo' },
  ];

  // ── Sugerencia de siguiente MC#### disponible ─────────────────────
  // Se llena cuando el validador async detecta que el ID escrito ya está
  // tomado por otro empleado. La UI muestra un enlace clickeable bajo el
  // aviso "Este ID ya está en uso por otro empleado" para aplicar el
  // consecutivo libre en un clic. Se cachea por sesión del formulario:
  // la siguiente vez que el usuario tropieza con otro ID ocupado se
  // reutiliza la misma sugerencia sin volver a pegarle al backend.
  siguienteIdSugerido = signal<string | null>(null);
  loadingSugerencia = signal(false);

  // Catálogos dinámicos
  areas = signal<CatalogoItem[]>([]);
  direcciones = signal<CatalogoItem[]>([]);
  puestos = signal<CatalogoItem[]>([]); // API catálogos central (positions)
  bancos = signal<CatalogoItem[]>([]); // API catálogos central (banks)
  generos = signal<CatalogoItem[]>([]); // API catálogos central (genders)
  estadosCiviles = signal<CatalogoItem[]>([]); // API catálogos central (marital-statuses)
  nacionalidades = signal<CatalogoItem[]>([]); // API catálogos central (nationalities)
  periodosPago = signal<CatalogoItem[]>([]); // API catálogos central (periodicities)
  esquemasPago = signal<CatalogoItem[]>([]); // API catálogos central (pay-schemes)
  tiposContrato = signal<CatalogoItem[]>([]); // API catálogos central (employment-contract-types)
  estadosMx = signal<CatalogoItem[]>([]); // API catálogos central (states-of-mexico)
  gradosEstudios = signal<CatalogoItem[]>([]); // API catálogos central (education-levels)
  parentescos = signal<CatalogoItem[]>([]); // API catálogos central (relationships)
  directores = signal<DirectorAsignacion[]>([]); // lista de directores para el dropdown de superior

  ngOnInit(): void {
    this.initForm();
    this.cargarCatalogos();
    this.observarSugerenciaIdInterno();

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.isEdit.set(true);
      this.empleadoId = +id;
      this.cargarEmpleado(this.empleadoId);
    }
  }

  /**
   * Vigila los cambios de status del control idInterno. Cuando el async
   * validator concluye con el error `idInternoTaken` cargamos la sugerencia
   * del siguiente consecutivo libre (una sola vez por sesión del form).
   * Si el usuario corrige y el error desaparece, dejamos la sugerencia en
   * caché — sigue siendo la misma para la próxima colisión.
   */
  private observarSugerenciaIdInterno(): void {
    const control = this.form.get('idInterno');
    if (!control) return;

    control.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (control.hasError('idInternoTaken')) {
        this.cargarSugerenciaIdInterno();
      }
    });
  }

  private cargarSugerenciaIdInterno(): void {
    if (this.siguienteIdSugerido() || this.loadingSugerencia()) return;
    this.loadingSugerencia.set(true);
    this.empleadoService
      .getNextInternalId('MC')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (next) => {
          this.siguienteIdSugerido.set(next);
          this.loadingSugerencia.set(false);
        },
        error: () => this.loadingSugerencia.set(false),
      });
  }

  /**
   * Aplica el ID sugerido al control `idInterno`. Se invoca desde el
   * enlace bajo el aviso de duplicado. Limpia la sugerencia tras usarla
   * para que el siguiente choque (raro) genere una nueva consulta.
   */
  aplicarSugerenciaIdInterno(): void {
    const sugerencia = this.siguienteIdSugerido();
    if (!sugerencia) return;
    const control = this.form.get('idInterno');
    if (!control) return;
    control.setValue(sugerencia);
    control.markAsDirty();
    control.markAsTouched();
    this.siguienteIdSugerido.set(null);
  }

  private cargarCatalogos(): void {
    this.loadingCatalogos.set(true);

    // Áreas y direcciones vienen de la BD propia (PostgreSQL)
    this.empleadoService
      .getCatalogos()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (cat) => {
          this.areas.set(cat.areas);
          this.direcciones.set(cat.direcciones);
          this.loadingCatalogos.set(false);
        },
        error: () => this.loadingCatalogos.set(false),
      });

    // Catálogos centrales SINAY, vía el proxy /api/RhCatalogos del backend de RH.
    // `RhCatalogosService` es GENERADO desde el contrato (schema-core): sabe las
    // rutas y las formas, y devuelve Promises. Cada catálogo se pide por separado
    // a propósito —ver `cargarCatalogo`—.
    this.cargarCatalogo(() => this.rhCatalogos.puestos(), this.puestos);
    this.cargarCatalogo(() => this.rhCatalogos.bancos(), this.bancos);

    // Datos personales: género, estado civil, nacionalidad
    this.cargarCatalogo(() => this.rhCatalogos.generos(), this.generos);
    this.cargarCatalogo(() => this.rhCatalogos.estadosCiviles(), this.estadosCiviles);
    this.cargarCatalogo(() => this.rhCatalogos.nacionalidades(), this.nacionalidades);

    // Datos laborales: periodo/esquema de pago, tipo de contrato
    this.cargarCatalogo(() => this.rhCatalogos.periodosPago(), this.periodosPago);
    this.cargarCatalogo(() => this.rhCatalogos.esquemasPago(), this.esquemasPago);
    this.cargarCatalogo(() => this.rhCatalogos.tiposContrato(), this.tiposContrato);

    // Domicilio: estados de México
    this.cargarCatalogo(() => this.rhCatalogos.estadosMx(), this.estadosMx);

    // FormArrays: grados de estudios, parentescos
    this.cargarCatalogo(() => this.rhCatalogos.gradosEstudios(), this.gradosEstudios);
    this.cargarCatalogo(() => this.rhCatalogos.parentescos(), this.parentescos);

    // Directores para el dropdown "Director / Superior" de Asignación y Pago.
    this.vacacionesService
      .getDirectores()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (data) => this.directores.set(data) });
  }

  /**
   * Pide un catálogo y lo deja en su signal.
   *
   * Uno por uno y con su propio `catch`, NO con un `Promise.all`: así el
   * comportamiento es el mismo que tenía con observables independientes —si un
   * catálogo falla, los otros diez siguen llegando—. Con `Promise.all` un solo
   * 500 vaciaría los once dropdowns.
   *
   * A diferencia de los `.subscribe()` que había aquí, esto no lleva
   * `takeUntilDestroyed`: una Promise no se cancela. Si el usuario sale del
   * formulario antes de que responda, el `set` cae sobre un signal huérfano, que
   * es inocuo. Es el mismo trato que hacen ffacturacion, fjuridico y FFIntelishs
   * con sus servicios generados.
   */
  private cargarCatalogo(
    pedir: () => Promise<CatalogoItem[] | null | undefined>,
    destino: WritableSignal<CatalogoItem[]>,
  ): void {
    pedir()
      .then((data) => destino.set(data ?? []))
      .catch(() => destino.set([]));
  }

  commitDateOnEnter(control: AbstractControl | null, event: Event): void {
    if (!control) return;

    if (event instanceof KeyboardEvent) {
      event.preventDefault();
      event.stopPropagation();
    }

    const target = event.target as HTMLElement | null;
    const currentTarget = event.currentTarget as HTMLElement | null;

    const input =
      (target?.tagName === 'INPUT' ? (target as HTMLInputElement) : null) ??
      (currentTarget?.querySelector('input') as HTMLInputElement | null);

    if (!input) return;

    const raw = input.value?.trim?.() ?? '';
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw);
    if (!match) {
      // Si no cumple el formato esperado, dejamos que el control/mensaje de validación haga su trabajo.
      input.blur();
      control.markAsTouched();
      return;
    }

    const day = Number(match[1]);
    const month = Number(match[2]);
    const year = Number(match[3]);
    const date = new Date(year, month - 1, day);

    const isValid =
      Number.isFinite(day) &&
      Number.isFinite(month) &&
      Number.isFinite(year) &&
      year >= 1900 &&
      year <= 2100 &&
      month >= 1 &&
      month <= 12 &&
      day >= 1 &&
      day <= 31 &&
      date.getFullYear() === year &&
      date.getMonth() === month - 1 &&
      date.getDate() === day;

    if (isValid) {
      control.setValue(date);
      control.markAsDirty();
      control.markAsTouched();
    } else {
      control.markAsTouched();
    }

    input.blur();
  }

  /**
   * Async validator: verifica con el backend si el idInterno ya está
   * tomado por OTRO empleado. Aplica debounce de 500ms para no spammear
   * mientras el usuario tipea. En modo edición, el ID del empleado actual
   * se excluye para que su propia clave no se cuente como duplicado.
   *
   * Error key emitido: `idInternoTaken: true` — el HTML lo muestra como
   * "Este ID ya está en uso por otro empleado".
   */
  private idInternoUniqueValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = (control.value as string | null)?.trim();
      // Skip si el valor es muy corto o vacío — el validador required ya cubre
      if (!value || value.length < 3) return of(null);

      // Excluir el propio empleado cuando ya tiene ID: en edición (route) o
      // en el alta por wizard tras crear en Paso 1 (empleadoId ya asignado).
      // Sin esto, al pasar al Paso 2 la clave recién creada se marcaría como
      // duplicada (existe en BD) y bloquearía el guardado complementario.
      const excludeId = this.empleadoId > 0 ? this.empleadoId : undefined;
      return timer(500).pipe(
        switchMap(() => this.empleadoService.checkInternalId(value, excludeId)),
        map((exists) => (exists ? { idInternoTaken: true } : null)),
        catchError(() => of(null)), // si la API falla, no bloquear el submit — el server vuelve a validar
      );
    };
  }

  /**
   * RFC y CURP a mayúsculas, sin espacios ni guiones, mientras se teclea y sin
   * que el cursor salte al final. Los validadores de `ui/forms` no aceptan
   * minúsculas, igual que la API.
   */
  protected aMayusculas(evento: Event, ruta: string): void {
    const input = evento.target as HTMLInputElement;
    const control = this.form.get(ruta);
    const valor = normalizarClave(input.value);
    if (control && valor !== control.value) {
      const { selectionStart, selectionEnd } = input;
      control.setValue(valor);
      input.setSelectionRange(selectionStart, selectionEnd);
    }
  }

  private initForm(): void {
    this.form = this.fb.group({
      // ── Campos requeridos por proc_alta_usuario ─────────────────────
      // idInterno: validadores sync (required, maxLength) + async (unicidad)
      idInterno: [
        '',
        [Validators.required, Validators.maxLength(20)],
        [this.idInternoUniqueValidator()],
      ],
      nombre: [
        '',
        [Validators.required, Validators.maxLength(100), Validators.pattern(PATRON_NOMBRE)],
      ],
      primerApellido: [
        '',
        [Validators.required, Validators.maxLength(100), Validators.pattern(PATRON_NOMBRE)],
      ],
      segundoApellido: ['', [Validators.maxLength(100), Validators.pattern(PATRON_NOMBRE)]],
      areaId: [null as number | null, [Validators.required, Validators.min(1)]],
      direccionId: [null as number | null, [Validators.required, Validators.min(1)]],
      // Director/superior (opcional). 0 = Director General. Se persiste en
      // movempleadojerarquia. Editable también desde "Cambiar Área".
      superiorId: [null as number | null],

      // ── Datos adicionales ────────────────────────────────────────────
      nombreCompleto: ['', [Validators.maxLength(250)]],
      // NSS / RFC / CURP opcionales: solo validan formato si traen valor (los
      // validadores de `ui/forms` aceptan vacío). El backend acepta
      // vacío-o-formato (regex "(...)?"), así que no hay 400 al dejarlos vacíos.
      //
      // Desde ui 5.2.33 son los de `@intelishs-group/ui/forms`: la CURP valida
      // entidad, fecha, sexo H/M/X y dígito verificador (antes rechazaba la X y
      // dejaba pasar `ZZZZ991399HZZZZZ01`); el RFC exige una fecha real. Sin `/i`:
      // RFC y CURP se pasan a mayúsculas al teclear (`aMayusculas`), porque la API
      // es case-sensitive y antes rechazaba lo que este formulario aceptaba.
      // ⚠️ La API (`CreateEmpleadoRequest.cs`) todavía rechaza la CURP con sexo X.
      nss: ['', [patron(RX_NSS, 'formato')]],
      rfc: ['', [rfc()]],
      curp: ['', [curp()]],
      // Los 3 catálogos personales se guardan como ID — el backend los
      // persiste en rh.catempleadorh.jdatos.fkid* y los resuelve a nombre
      // al leer. Migrado del patrón "string=descripción" en el Bloque #4.
      sexoId: [null as number | null],
      estadoCivilId: [null as number | null],
      lugarNacimiento: [''],
      nacionalidadId: [null as number | null],
      fechaIngreso: [null as Date | null],

      // Datos laborales
      datosLaborales: this.fb.group({
        direccion: [''],
        area: [''],
        // Puesto como ID — se persiste en rh.movempleadopuesto.fkidpuesto
        // (UPDATE de la fila activa). Mismo patrón que sexo/estadocivil/etc.
        puestoId: [null as number | null],
        // periodo/esquema de pago también como IDs (jdatos.fkidperiodopago /
        // fkidesquemapago). Bloque #4.
        periodoPagoId: [null as number | null],
        esquemaPagoId: [null as number | null],
        tipoContrato: [''],
        duracionContrato: [''],
        // Bloque #6: horario estructurado. nz-time-picker maneja Date; el
        // mapeo a string "HH:mm" se hace en guardar() antes de enviar.
        // Se persiste en rh.movempleadohorario.thorainicio/thorafin.
        horaInicio: [null as Date | null],
        horaFin: [null as Date | null],
        // nz-select nzMode="multiple" con IDs 1=Lunes…7=Domingo. Se persiste
        // en rh.movempleadohorario.jdatos.idiasdescanso (array de int).
        diasDescansoIds: [[] as number[]],
      }),

      // Datos salariales
      datosSalariales: this.fb.group({
        sdNominal: [null as number | null, [Validators.min(0)]],
        sdIntegrado: [null as number | null, [Validators.min(0)]],
        sueldoNetoImss: [null as number | null, [Validators.min(0)]],
      }),

      // Domicilio
      domicilio: this.fb.group({
        calle: [''],
        numeroExt: [''],
        numeroInt: [''],
        colonia: [''],
        poblacion: [''],
        estado: [''],
        codigoPostal: ['', [patron(RX_CODIGO_POSTAL, 'formato')]],
        telefono: ['', [patron(RX_TELEFONO_MX, 'formato')]],
        // El email es OPCIONAL desde que el alta dejo de pasar por
        // `rh.proc_alta_usuario` (2026-10-28). Aquel lo exigia porque fabricaba
        // con el el `scorreo` del usuario —mal: lo ignoraba y armaba uno
        // `@mc.com`—. Ahora lo recibe identidad, y si no llega deriva un
        // provisional `<usuario>@sinay.invalid` que se ve a leguas que lo es.
        //
        // Se deja vacio a proposito en un caso real: un empleado puede entrar
        // antes de que le asignen el correo de empresa. El formato SI se valida
        // cuando se escribe algo.
        email: ['', [Validators.email]],
      }),

      // Datos bancarios
      datosBancarios: this.fb.group({
        banco: [''],
        numeroCuenta: [''],
        // Con dígito verificador (Banxico): 18 dígitos cualquiera ya no pasan.
        clabeInterbancaria: ['', [clabe()]],
      }),

      // INFONAVIT
      infonavit: this.fb.group({
        numeroCredito: [''],
        tipoRetencion: [''],
        importeVsmCuota: [''],
      }),

      // Listas
      pagadoras: this.fb.array([]),
      contactosEmergencia: this.fb.array([]),
      estudios: this.fb.array([]),
    });
  }

  // ── FormArrays helpers ──────────────────────────────────────────────

  get pagadoras(): FormArray {
    return this.form.get('pagadoras') as FormArray;
  }

  get contactosEmergencia(): FormArray {
    return this.form.get('contactosEmergencia') as FormArray;
  }

  get estudios(): FormArray {
    return this.form.get('estudios') as FormArray;
  }

  agregarPagadora(): void {
    this.pagadoras.push(
      this.fb.group({
        // id (kidpagadora) — null en filas nuevas; el backend lo asigna en POST.
        id: [null as number | null],
        // salias es NOT NULL en BD → required.
        pagadora: ['', Validators.required],
        // fechaAltaImss (ffechaimss) es NOT NULL en BD; si el form lo deja en
        // null, el backend defaultea a FechaHoyMexico() — no marcamos required.
        fechaAltaImss: [null as Date | null],
        fechaBajaImss: [null as Date | null],
        // La primera pagadora del empleado nace como la actual; si ya hay una,
        // la nueva no le quita la marca a menos que se prenda a propósito.
        esActual: [!this.pagadoras.controls.some((g) => g.value.esActual)],
        bancoId: [null as number | null],
        empresaId: [null as number | null],
      }),
    );
  }

  /** Quita del FormArray una fila que todavía no se ha guardado. */
  descartarPagadora(i: number): void {
    if (this.pagadoras.at(i).value.id != null) return;
    this.pagadoras.removeAt(i);
    this.limpiarPagadoraSavingTrasRemove(i);
  }

  // ── Switch "Actual" ──
  //
  // Solo puede haber una pagadora actual. En una fila guardada el switch aplica
  // AL MOMENTO (es un switch, no un checkbox de formulario): prenderla apaga a
  // la que lo era; apagarla solo cambia esa fila. Usa `nzControl`, así que el
  // clic no mueve el switch: lo mueve la respuesta del backend.
  //
  // Va por el mismo PUT de la fila, que manda todos sus campos. Por eso se
  // bloquea mientras la fila tenga cambios sin guardar: si no, prender el switch
  // guardaría de paso un nombre o una fecha a medio capturar.

  switchActualBloqueado(i: number): boolean {
    const grupo = this.pagadoras.at(i);
    return grupo.value.id != null && grupo.dirty;
  }

  tooltipActual(i: number): string {
    const grupo = this.pagadoras.at(i);
    if (this.switchActualBloqueado(i)) {
      return 'Guarda primero los cambios de esta fila para cambiar la pagadora actual';
    }
    if (grupo.value.id == null) {
      return 'Se aplica al guardar la fila. Solo puede haber una pagadora actual: si la prendes, la actual deja de serlo';
    }
    return grupo.value.esActual
      ? 'Es la pagadora actual del empleado. Apagarla no la elimina: se queda configurada y el empleado queda sin pagadora actual'
      : 'Marcarla como la pagadora actual del empleado. Solo puede haber una: la actual deja de serlo';
  }

  cambiarActual(i: number): void {
    const grupo = this.pagadoras.at(i) as FormGroup;
    if (this.estaGuardandoPagadora(i) || this.switchActualBloqueado(i)) return;

    const nuevo = !grupo.value.esActual;

    // Fila nueva: es un valor más del formulario, viaja con Guardar.
    if (grupo.value.id == null) {
      grupo.get('esActual')!.setValue(nuevo);
      return;
    }

    const dto: Pagadora = { ...this.pagadoraDto(grupo), esActual: nuevo };

    this.setPagadoraSaving(i, true);
    this.empleadoService
      .actualizarPagadora(this.empleadoId, grupo.value.id, dto)
      .pipe(
        finalize(() => this.setPagadoraSaving(i, false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          // setValue no marca la fila como modificada: el valor ya está en BD.
          grupo.get('esActual')!.setValue(nuevo);
          if (nuevo) this.apagarOtrasPagadoras(i);
          this.message.success(
            nuevo
              ? `${grupo.value.pagadora} es ahora la pagadora actual`
              : `${grupo.value.pagadora} ya no es la pagadora actual`,
          );
        },
        error: (err) => {
          this.message.error(this.extraerError(err) ?? 'No se pudo cambiar la pagadora actual');
        },
      });
  }

  /** Refleja en pantalla lo que el backend ya hizo al marcar la fila `i`. */
  private apagarOtrasPagadoras(i: number): void {
    this.pagadoras.controls.forEach((g, j) => {
      if (j !== i && g.value.esActual) g.get('esActual')!.setValue(false);
    });
  }

  private pagadoraDto(grupo: FormGroup): Pagadora {
    const v = grupo.value as {
      id: number | null;
      pagadora: string;
      fechaAltaImss: Date | string | null;
      fechaBajaImss: Date | string | null;
      esActual: boolean;
      bancoId: number | null;
      empresaId: number | null;
    };
    return {
      id: v.id ?? undefined,
      pagadora: v.pagadora,
      fechaAltaImss: this.toIsoDate(v.fechaAltaImss),
      fechaBajaImss: this.toIsoDate(v.fechaBajaImss),
      esActual: v.esActual,
      bancoId: v.bancoId ?? undefined,
      empresaId: v.empresaId ?? undefined,
    };
  }

  /**
   * Guarda una pagadora (alta o edición). POST si la fila no tiene `id`; PUT
   * si ya lo tiene. Tras el alta exitosa patchea el `id` devuelto.
   */
  guardarPagadora(i: number): void {
    const grupo = this.pagadoras.at(i) as FormGroup;
    if (grupo.invalid) {
      grupo.markAllAsTouched();
      this.message.warning('Captura el nombre (alias) de la pagadora.');
      return;
    }
    if (this.empleadoId <= 0) {
      this.message.error('Guarda primero los datos principales del empleado.');
      return;
    }

    const v = grupo.value as { id: number | null; fechaAltaImss: Date | string | null };
    const dto = this.pagadoraDto(grupo);

    const tieneId = v.id != null;
    const op$ = tieneId
      ? this.empleadoService.actualizarPagadora(this.empleadoId, v.id!, dto)
      : this.empleadoService.crearPagadora(this.empleadoId, dto);

    this.setPagadoraSaving(i, true);
    op$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.setPagadoraSaving(i, false)),
      )
      .subscribe({
        next: (guardado) => {
          // Patchear id + fechaAltaImss (por si el backend la defaulteó a CDMX).
          grupo.patchValue({
            id: guardado.id ?? v.id,
            fechaAltaImss: guardado.fechaAltaImss
              ? new Date(guardado.fechaAltaImss)
              : v.fechaAltaImss,
          });
          // Ya está en BD: el switch "Actual" vuelve a quedar disponible.
          grupo.markAsPristine();
          if (dto.esActual) this.apagarOtrasPagadoras(i);
          this.message.success(tieneId ? 'Pagadora actualizada' : 'Pagadora agregada');
        },
        error: (err) => {
          this.message.error(this.extraerError(err) ?? 'No se pudo guardar la pagadora');
        },
      });
  }

  /** Convierte Date/string a "YYYY-MM-DD" para enviar fechas a la API. */
  private toIsoDate(v: Date | string | null | undefined): string | undefined {
    if (!v) return undefined;
    const d = v instanceof Date ? v : new Date(v);
    if (isNaN(d.getTime())) return undefined;
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  // Estado "guardando…" por fila de pagadora (mismo patrón que contactos).
  pagadoraSaving = signal<ReadonlySet<number>>(new Set());

  estaGuardandoPagadora(i: number): boolean {
    return this.pagadoraSaving().has(i);
  }

  private setPagadoraSaving(i: number, saving: boolean): void {
    this.pagadoraSaving.update((s) => {
      const next = new Set(s);
      if (saving) next.add(i);
      else next.delete(i);
      return next;
    });
  }

  private limpiarPagadoraSavingTrasRemove(i: number): void {
    this.pagadoraSaving.update((s) => {
      const next = new Set<number>();
      s.forEach((idx) => {
        if (idx < i) next.add(idx);
        else if (idx > i) next.add(idx - 1);
      });
      return next;
    });
  }

  agregarContacto(): void {
    this.contactosEmergencia.push(
      this.fb.group({
        // id (kidempleadocontacto) — null en filas nuevas; el backend lo asigna
        // tras "Guardar" y el form lo patchea para que la siguiente edición sea
        // UPDATE en vez de INSERT.
        id: [null as number | null],
        nombre: [
          '',
          [Validators.required, Validators.maxLength(100), Validators.pattern(PATRON_NOMBRE)],
        ],
        // parentescoId (fkidparentesco) es NOT NULL en BD → obligatorio.
        parentescoId: [null as number | null, Validators.required],
        telefono: ['', [patron(RX_TELEFONO_MX, 'formato')]],
      }),
    );
  }

  /**
   * Eliminar contacto: si la fila ya está en BD (tiene `id`), hace soft-delete
   * en el backend (`UPDATE bactivo = false`). Si es una fila nueva sin guardar,
   * solo la quita del FormArray.
   */
  eliminarContacto(i: number): void {
    const grupo = this.contactosEmergencia.at(i) as FormGroup;
    const id = grupo.value.id as number | null;

    if (!id || this.empleadoId <= 0) {
      this.contactosEmergencia.removeAt(i);
      this.limpiarContactoSavingTrasRemove(i);
      return;
    }

    this.setContactoSaving(i, true);
    this.empleadoService
      .eliminarContacto(this.empleadoId, id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.contactosEmergencia.removeAt(i);
          this.limpiarContactoSavingTrasRemove(i);
          this.message.success('Contacto eliminado');
        },
        error: (err) => {
          this.setContactoSaving(i, false);
          this.message.error(this.extraerError(err) ?? 'No se pudo eliminar el contacto');
        },
      });
  }

  /**
   * Guarda un contacto (alta o edición). Si la fila no tiene `id` hace POST;
   * si ya tiene, PUT. Tras el POST exitoso patchea el `id` devuelto para que
   * los siguientes guardados de esa fila sean UPDATE.
   */
  guardarContacto(i: number): void {
    const grupo = this.contactosEmergencia.at(i) as FormGroup;
    if (grupo.invalid) {
      grupo.markAllAsTouched();
      this.message.warning('Completa nombre y parentesco del contacto.');
      return;
    }
    if (this.empleadoId <= 0) {
      this.message.error('Guarda primero los datos principales del empleado.');
      return;
    }

    const v = grupo.value as {
      id: number | null;
      nombre: string;
      parentescoId: number | null;
      telefono: string | null;
    };
    const dto: ContactoEmergencia = {
      id: v.id ?? undefined,
      nombre: v.nombre,
      // `parentescoId` es obligatorio en el contrato y el control es
      // `Validators.required`; el guard de `grupo.invalid` de arriba ya
      // descartó el caso nulo, así que aquí no puede serlo.
      parentescoId: v.parentescoId!,
      telefono: v.telefono ?? undefined,
    };

    const tieneId = v.id != null;
    const op$ = tieneId
      ? this.empleadoService.actualizarContacto(this.empleadoId, v.id!, dto)
      : this.empleadoService.crearContacto(this.empleadoId, dto);

    this.setContactoSaving(i, true);
    op$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.setContactoSaving(i, false)),
      )
      .subscribe({
        next: (guardado) => {
          // Patchear con el id devuelto (clave en alta para que el próximo
          // guardado sea UPDATE) y refrescar parentescoId por si el backend
          // normalizó.
          grupo.patchValue({
            id: guardado.id ?? v.id,
            parentescoId: guardado.parentescoId ?? v.parentescoId,
          });
          this.message.success(tieneId ? 'Contacto actualizado' : 'Contacto agregado');
        },
        error: (err) => {
          this.message.error(this.extraerError(err) ?? 'No se pudo guardar el contacto');
        },
      });
  }

  // ── Estado "guardando…" por fila de contacto ─────────────────────
  // Indexado por posición en el FormArray. Se reindexa al eliminar filas.
  contactoSaving = signal<ReadonlySet<number>>(new Set());

  estaGuardandoContacto(i: number): boolean {
    return this.contactoSaving().has(i);
  }

  private setContactoSaving(i: number, saving: boolean): void {
    this.contactoSaving.update((s) => {
      const next = new Set(s);
      if (saving) next.add(i);
      else next.delete(i);
      return next;
    });
  }

  /** Tras `removeAt(i)`, los índices >= i+1 se corren -1; reflejamos eso aquí. */
  private limpiarContactoSavingTrasRemove(i: number): void {
    this.contactoSaving.update((s) => {
      const next = new Set<number>();
      s.forEach((idx) => {
        if (idx < i) next.add(idx);
        else if (idx > i) next.add(idx - 1);
        // idx === i → se descarta (la fila ya no existe).
      });
      return next;
    });
  }

  /** Extrae un mensaje legible de errores del backend (ApiResponse o ProblemDetails). */
  private extraerError(err: unknown): string | null {
    const e = err as {
      error?: {
        errorMessage?: string;
        detail?: string;
        title?: string;
        errors?: Record<string, string[]>;
      };
    };
    if (e?.error?.errors) {
      const primero = Object.values(e.error.errors)[0]?.[0];
      if (primero) return primero;
    }
    return e?.error?.errorMessage ?? e?.error?.detail ?? e?.error?.title ?? null;
  }

  agregarEstudio(): void {
    this.estudios.push(
      this.fb.group({
        // id (kidempleadoestudio) — null en filas nuevas; el backend lo asigna en POST.
        id: [null as number | null],
        // fkidgradoestudio es NOT NULL en BD → required.
        gradoEstudiosId: [null as number | null, Validators.required],
        campoEstudio: [''],
      }),
    );
  }

  /**
   * Eliminar estudio: soft-delete vía backend si la fila ya está en BD;
   * de lo contrario solo la quita del FormArray.
   */
  eliminarEstudio(i: number): void {
    const grupo = this.estudios.at(i) as FormGroup;
    const id = grupo.value.id as number | null;

    if (!id || this.empleadoId <= 0) {
      this.estudios.removeAt(i);
      this.limpiarEstudioSavingTrasRemove(i);
      return;
    }

    this.setEstudioSaving(i, true);
    this.empleadoService
      .eliminarEstudio(this.empleadoId, id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.estudios.removeAt(i);
          this.limpiarEstudioSavingTrasRemove(i);
          this.message.success('Estudio eliminado');
        },
        error: (err) => {
          this.setEstudioSaving(i, false);
          this.message.error(this.extraerError(err) ?? 'No se pudo eliminar el estudio');
        },
      });
  }

  /**
   * Guarda un estudio (alta o edición). POST si la fila no tiene `id`; PUT si
   * ya lo tiene. Tras el alta exitosa patchea el `id` devuelto.
   */
  guardarEstudio(i: number): void {
    const grupo = this.estudios.at(i) as FormGroup;
    if (grupo.invalid) {
      grupo.markAllAsTouched();
      this.message.warning('Selecciona el grado de estudios.');
      return;
    }
    if (this.empleadoId <= 0) {
      this.message.error('Guarda primero los datos principales del empleado.');
      return;
    }

    const v = grupo.value as {
      id: number | null;
      gradoEstudiosId: number | null;
      campoEstudio: string | null;
    };

    const dto: Estudio = {
      id: v.id ?? undefined,
      // Obligatorio en el contrato, `Validators.required` en el control y el
      // guard de `grupo.invalid` ya retornó: no puede llegar nulo.
      gradoEstudiosId: v.gradoEstudiosId!,
      campoEstudio: v.campoEstudio ?? undefined,
    };

    const tieneId = v.id != null;
    const op$ = tieneId
      ? this.empleadoService.actualizarEstudio(this.empleadoId, v.id!, dto)
      : this.empleadoService.crearEstudio(this.empleadoId, dto);

    this.setEstudioSaving(i, true);
    op$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.setEstudioSaving(i, false)),
      )
      .subscribe({
        next: (guardado) => {
          grupo.patchValue({
            id: guardado.id ?? v.id,
            gradoEstudiosId: guardado.gradoEstudiosId ?? v.gradoEstudiosId,
          });
          this.message.success(tieneId ? 'Estudio actualizado' : 'Estudio agregado');
        },
        error: (err) => {
          this.message.error(this.extraerError(err) ?? 'No se pudo guardar el estudio');
        },
      });
  }

  // Estado "guardando…" por fila de estudio (mismo patrón que contactos/pagadoras).
  estudioSaving = signal<ReadonlySet<number>>(new Set());

  estaGuardandoEstudio(i: number): boolean {
    return this.estudioSaving().has(i);
  }

  private setEstudioSaving(i: number, saving: boolean): void {
    this.estudioSaving.update((s) => {
      const next = new Set(s);
      if (saving) next.add(i);
      else next.delete(i);
      return next;
    });
  }

  private limpiarEstudioSavingTrasRemove(i: number): void {
    this.estudioSaving.update((s) => {
      const next = new Set<number>();
      s.forEach((idx) => {
        if (idx < i) next.add(idx);
        else if (idx > i) next.add(idx - 1);
      });
      return next;
    });
  }

  // ── Cargar empleado para edición ────────────────────────────────────

  private cargarEmpleado(id: number): void {
    this.loading.set(true);
    this.empleadoService
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (emp) => {
          // Los nombres separados vienen del backend en `EmpleadoResponse`,
          // desde `rh.catempleadorh` (`snombresplit` / `sprimerapellido` /
          // `ssegundoapellido`), que es donde los escriben el alta y la edición.
          //
          // El fallback que parte `nombreCompleto` sigue aquí para el empleado
          // LEGACY dado de alta antes de que existieran esas columnas y que
          // nunca se editó. Ya no es el caso normal —lo fue mientras el
          // expediente no devolvía estos campos, y repartía mal cualquier nombre
          // compuesto: "María del Carmen García López" salía como nombre "María"
          // y primer apellido "del"—. En cuanto ese empleado se guarda una vez,
          // las columnas quedan pobladas y este bloque deja de entrar.
          let nombre = emp.nombre ?? '';
          let primerApellido = emp.primerApellido ?? '';
          let segundoApellido = emp.segundoApellido ?? '';

          if (!nombre && !primerApellido && !segundoApellido && emp.nombreCompleto) {
            const partes = emp.nombreCompleto.trim().split(/\s+/);
            nombre = partes[0] ?? '';
            primerApellido = partes[1] ?? '';
            segundoApellido = partes.slice(2).join(' ');
          }

          // Intentar encontrar los IDs correspondientes en los catálogos cargados
          const areaId =
            this.areas().find((a) => a.descripcion === emp.datosLaborales?.area)?.id ?? null;
          const direccionId =
            this.direcciones().find((d) => d.descripcion === emp.datosLaborales?.direccion)?.id ??
            null;

          // Bloque #6: mapear "HH:mm" del response a Date local para que nz-time-picker
          // los muestre, y normalizar diasDescansoIds (default a [] si viene null).
          const dl = emp.datosLaborales ?? {};
          const datosLaboralesForm = {
            ...dl,
            horaInicio: this.hhmmToDate(dl.horaInicio),
            horaFin: this.hhmmToDate(dl.horaFin),
            diasDescansoIds: dl.diasDescansoIds ?? [],
          };

          this.form.patchValue({
            idInterno: emp.idInterno,
            nombre,
            primerApellido,
            segundoApellido,
            areaId,
            direccionId,
            superiorId: emp.superiorId ?? null,
            nombreCompleto: emp.nombreCompleto,
            nss: emp.nss,
            rfc: emp.rfc,
            curp: emp.curp,
            // Bloque #4: usamos los IDs que llegan del backend para preseleccionar
            // dropdowns (el fallback por descripción quedó descartado — frágil).
            sexoId: emp.sexoId ?? null,
            estadoCivilId: emp.estadoCivilId ?? null,
            lugarNacimiento: emp.lugarNacimiento,
            nacionalidadId: emp.nacionalidadId ?? null,
            fechaIngreso: emp.fechaIngreso ? new Date(emp.fechaIngreso) : null,
            datosLaborales: datosLaboralesForm,
            datosSalariales: emp.datosSalariales ?? {},
            domicilio: emp.domicilio ?? {},
            datosBancarios: emp.datosBancarios ?? {},
            infonavit: emp.infonavit ?? {},
          });

          // Pagadoras — id + bancoId + empresaId vienen del backend para que los
          // endpoints por fila distingan INSERT vs UPDATE y conserven catálogos.
          emp.pagadoras?.forEach((p) => {
            this.pagadoras.push(
              this.fb.group({
                id: [p.id ?? null],
                // salias es NOT NULL → required en el form también.
                pagadora: [p.pagadora ?? '', Validators.required],
                fechaAltaImss: [p.fechaAltaImss ? new Date(p.fechaAltaImss) : null],
                fechaBajaImss: [p.fechaBajaImss ? new Date(p.fechaBajaImss) : null],
                esActual: [p.esActual],
                bancoId: [p.bancoId ?? null],
                empresaId: [p.empresaId ?? null],
              }),
            );
          });

          // Contactos — id + parentescoId vienen del backend (kidempleadocontacto,
          // fkidparentesco) para que "Guardar" decida POST vs PUT y el dropdown
          // se preseleccione por ID. parentescoId es Validators.required.
          emp.contactosEmergencia?.forEach((c) => {
            this.contactosEmergencia.push(
              this.fb.group({
                id: [c.id ?? null],
                nombre: [
                  c.nombre,
                  [
                    Validators.required,
                    Validators.maxLength(100),
                    Validators.pattern(PATRON_NOMBRE),
                  ],
                ],
                parentescoId: [c.parentescoId ?? null, Validators.required],
                telefono: [c.telefono, [patron(RX_TELEFONO_MX, 'formato')]],
              }),
            );
          });

          // Estudios — id + gradoEstudiosId vienen del backend (kidempleadoestudio,
          // fkidgradoestudio) para que el dropdown se preseleccione por ID y
          // "Guardar" decida POST vs PUT. gradoEstudiosId es Validators.required.
          emp.estudios?.forEach((es) => {
            this.estudios.push(
              this.fb.group({
                id: [es.id ?? null],
                gradoEstudiosId: [es.gradoEstudiosId ?? null, Validators.required],
                campoEstudio: [es.campoEstudio],
              }),
            );
          });

          this.loading.set(false);
        },
        error: () => {
          this.message.error('Error al cargar empleado');
          this.loading.set(false);
        },
      });
  }

  // ── Wizard de alta: Paso 1 (crea con el SP) ──────────────────────────

  /**
   * Paso 1 del alta: valida solo los campos que el SP necesita y llama a
   * create(). El backend corre proc_alta_usuario (crea catusuario +
   * catempleadorh + relación área/dirección) y devuelve el empleado con su
   * ID. Guardamos ese ID y avanzamos al Paso 2, donde el resto se persiste
   * con update() sobre el empleado ya existente.
   */
  crearPaso1(): void {
    this.submitted.set(true);

    // Espera validadores async (unicidad de idInterno) antes de decidir.
    if (this.form.pending) {
      this.message.info('Verificando datos, espera un momento...');
      this.form.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status) => {
        if (status !== 'PENDING') this.crearPaso1();
      });
      return;
    }

    // Validar SOLO los controles del Paso 1 (el resto se valida en Paso 2).
    let hayInvalidos = false;
    for (const path of this.controlesPaso1) {
      const control = this.form.get(path);
      if (control && control.invalid) {
        control.markAsDirty();
        control.markAsTouched();
        hayInvalidos = true;
      }
    }
    if (hayInvalidos) {
      this.message.warning('Revisa los campos marcados en rojo');
      return;
    }

    this.saving.set(true);
    const request = this.construirPayload() as unknown as CreateEmpleadoRequest;
    this.empleadoService
      .create(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (emp) => {
          this.empleadoId = emp.id;
          this.saving.set(false);
          this.submitted.set(false); // reset para la validación del Paso 2
          this.paso.set(2);

          // ── Las credenciales, antes de cualquier otra cosa ───────────────
          //
          // Existen SOLO en esta respuesta: de la base sale el hash y nada más.
          // Si no se entregan aquí, el empleado queda dado de alta y sin poder
          // entrar, y eso no se descubre hasta que lo intenta.
          //
          if (emp.usuarioSinay && emp.contrasenaInicial) {
            this.contrasenaCopiada.set(false);
            this.credenciales.set({
              usuario: emp.usuarioSinay,
              contrasena: emp.contrasenaInicial,
              nombre: [emp.nombre, emp.primerApellido].filter(Boolean).join(' '),
            });
          } else {
            // Una API anterior al cambio no las manda. El alta es válida —el
            // empleado existe— pero alguien tiene que reponerle la contraseña,
            // así que se dice en vez de pasar de largo.
            this.message.warning(
              'El empleado se creó, pero no se recibieron sus credenciales de SINAY. ' +
                'Repónselas desde el portal de administración.',
            );
          }

          this.message.success('Empleado creado. Completa la información complementaria.');
        },
        error: (err) => {
          this.message.error(this.extraerMensajeError(err, 'Error al crear empleado'));
          this.saving.set(false);
        },
      });
  }

  // ── Guardar (Paso 2 del alta, o edición) ─────────────────────────────

  /**
   * Persiste vía update() cuando el empleado ya existe — tanto en edición
   * (ID viene de la ruta) como en el Paso 2 del alta (ID viene del create
   * del Paso 1). Es el único camino que escribe las tablas mov* (ingreso,
   * domicilio, horario, puesto, etc.) porque necesitan el kidempleadorh.
   */
  guardar(): void {
    this.submitted.set(true);

    // Si hay validadores async pendientes (típicamente el de unicidad de
    // idInterno, con debounce 500ms), espera a que terminen antes de
    // decidir si el form es válido.
    if (this.form.pending) {
      this.message.info('Verificando datos, espera un momento...');
      this.form.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((status) => {
        if (status !== 'PENDING') this.guardar();
      });
      return;
    }

    if (this.form.invalid) {
      this.marcarControlesComoSucios(this.form);
      this.message.warning('Revisa los campos marcados en rojo');
      return;
    }

    this.saving.set(true);
    const formValue = this.construirPayload();

    // En edición el ID viene de la ruta; en el Paso 2 del alta viene del
    // create() del Paso 1. En ambos casos empleadoId > 0 → update().
    const request = { ...formValue, id: this.empleadoId } as unknown as UpdateEmpleadoRequest;
    this.empleadoService
      .update(this.empleadoId, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.message.success(this.isEdit() ? 'Empleado actualizado' : 'Empleado guardado');
          this.saving.set(false);
          this.salir();
        },
        error: (err) => {
          // Extrae el mensaje específico del backend. Fallback al texto
          // genérico si el response no tiene el shape esperado.
          this.message.error(this.extraerMensajeError(err, 'Error al guardar empleado'));
          this.saving.set(false);
        },
      });
  }

  /**
   * Lee el form completo y mapea las horas de nz-time-picker (Date) a
   * string "HH:mm" que espera el backend. Compartido por crearPaso1() y
   * guardar() para no duplicar la transformación.
   */
  private construirPayload(): Record<string, unknown> {
    const formValue = this.form.getRawValue();
    const dl = formValue.datosLaborales as Record<string, unknown> | null | undefined;
    if (dl) {
      dl['horaInicio'] = this.dateToHHmm(dl['horaInicio'] as Date | null);
      dl['horaFin'] = this.dateToHHmm(dl['horaFin'] as Date | null);
    }
    return formValue;
  }

  // ── Helpers para nz-time-picker (Bloque #6) ───────────────────────
  /**
   * Convierte un string "HH:mm" a Date local (hoy con la hora indicada).
   * nz-time-picker lo necesita como Date para inicializar/mostrar el valor.
   * Devuelve null si el string no cumple el formato.
   */
  private hhmmToDate(hhmm: string | null | undefined): Date | null {
    if (!hhmm) return null;
    const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hhmm);
    if (!m) return null;
    const d = new Date();
    d.setHours(Number(m[1]), Number(m[2]), 0, 0);
    return d;
  }

  /**
   * Convierte una Date de nz-time-picker a string "HH:mm" para enviar al
   * backend (formato que <c>thorainicio</c>/<c>thorafin</c> espera).
   */
  private dateToHHmm(d: Date | null | undefined): string | null {
    if (!d) return null;
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }

  /**
   * Extrae un mensaje legible del error del backend. Maneja dos formatos:
   *  1. ResponseDto nuestro: { errorMessage: "..." }.
   *  2. ProblemDetails de ASP.NET (validación de modelo, 400):
   *     { errors: { Campo: ["msg", ...] }, title: "..." } — concatena todos
   *     los mensajes de validación para que el usuario vea qué campo falló
   *     en vez del fallback genérico.
   * Si nada calza, regresa el fallback.
   */
  private extraerMensajeError(err: unknown, fallback: string): string {
    if (!err || typeof err !== 'object' || !('error' in err)) return fallback;

    const body = (err as { error?: unknown }).error;
    if (!body || typeof body !== 'object') return fallback;

    // 1. Nuestro ResponseDto
    const rd = body as { errorMessage?: string };
    if (rd.errorMessage && typeof rd.errorMessage === 'string') {
      return rd.errorMessage;
    }

    // 2. ProblemDetails de validación (.NET): { errors: { Campo: [..] } }
    const pd = body as { errors?: Record<string, string[]>; title?: string };
    if (pd.errors && typeof pd.errors === 'object') {
      const mensajes = Object.values(pd.errors).flat().filter(Boolean);
      if (mensajes.length > 0) return mensajes.join(' · ');
    }
    if (pd.title && typeof pd.title === 'string') {
      return pd.title;
    }

    return fallback;
  }

  cancelar(): void {
    this.salir();
  }

  /**
   * Sale del formulario hacia el detalle del empleado cuando ya existe
   * (edición, o alta tras crear en el Paso 1) — así el usuario ve de
   * inmediato cómo quedó. Si todavía no hay empleado (alta en Paso 1 sin
   * crear), regresa al listado porque no hay detalle que mostrar.
   */
  private salir(): void {
    if (this.empleadoId > 0) {
      this.router.navigate(['/empleados/detalle', this.empleadoId]);
    } else {
      this.router.navigate(['/empleados']);
    }
  }

  private marcarControlesComoSucios(group: FormGroup | FormArray): void {
    Object.values(group.controls).forEach((control) => {
      if (control instanceof FormGroup || control instanceof FormArray) {
        this.marcarControlesComoSucios(control);
      } else {
        control.markAsDirty();
        control.updateValueAndValidity();
      }
    });
  }
}
